import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  Public,
  Newspaper,
} from '@mui/icons-material'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import DeselectIcon from '@mui/icons-material/Deselect'
import { useNewsManagement } from '../../../hooks/useNewsManagement'
import { useResponsivePageSize } from '../../../hooks/useResponsivePageSize'
import { NEWS_REVIEW_STATUS } from '../../../constants/newsReviewStatus'
import NewsArticleCard from './NewsArticleCard'
import NewsFiltersBar from './NewsFiltersBar'
import Pagination from '../../../../Pagination'
import ItemsPerPageSelector from '../../../../ItemsPerPageSelector'

const GCC_COUNTRIES = ['Bahrain', 'KSA', 'Kuwait', 'Oman', 'Qatar', 'UAE']

const GCC_FLAG_CODES = {
  Bahrain: 'bh', KSA: 'sa', Kuwait: 'kw', Oman: 'om', Qatar: 'qa', UAE: 'ae',
}

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}
const CardSkeleton = () => (
  <Box
    sx={{
      border: '1px solid #e2e8f0',
      borderRadius: 3,
      p: 2.5,
      height: 280,
    }}
  >
    <Skeleton width='60%' height={20} sx={{ mb: 1 }} />
    <Skeleton width='100%' height={20} sx={{ mb: 0.5 }} />
    <Skeleton width='100%' height={20} sx={{ mb: 0.5 }} />
    <Skeleton width='80%' height={20} sx={{ mb: 2 }} />
    <Skeleton width='40%' height={20} sx={{ mb: 1 }} />
    <Skeleton width='100%' height={16} />
  </Box>
)

const EmptyState = ({ filters }) => (
  <Box
    sx={{
      textAlign: 'center',
      py: 8,
      px: 3,
      border: '2px dashed #e2e8f0',
      borderRadius: 3,
      bgcolor: '#f8fafc',
    }}
  >
    <Newspaper sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
    <Typography variant='h6' sx={{ color: '#475569', fontWeight: 600, mb: 1 }}>
      No articles found
    </Typography>
    <Typography variant='body2' color='text.secondary'>
      {filters.status === 'PENDING_REVIEW'
        ? 'No articles are currently pending review. All low-confidence news has been actioned.'
        : 'No articles match the current filters. Try adjusting your search or filters.'}
    </Typography>
  </Box>
)

// Persistent selection + bulk action bar
const BulkActionBar = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onBulkApprove,
  onBulkReject,
  onBulkAssignCountry,
}) => {
  const noneSelected = selectedCount === 0
  const allSelected = selectedCount > 0 && selectedCount === totalCount

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        mb: 2,
        borderRadius: '10px',
        border: '1.5px solid',
        borderColor: noneSelected ? '#e2e8f0' : '#232536',
        bgcolor: noneSelected ? '#f8fafc' : '#fff',
        boxShadow: noneSelected
          ? '0 1px 4px rgba(35,37,54,0.04)'
          : '0 1px 6px rgba(35,37,54,0.1)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        flexWrap: 'wrap',
      }}
    >
      {/* Approve */}
      <Tooltip title={noneSelected ? 'Select articles first' : ''}>
        <span>
          <Button
            size='small'
            startIcon={<CheckCircle sx={{ fontSize: 15 }} />}
            onClick={onBulkApprove}
            disabled={noneSelected}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 1.5,
              py: 0.6,
              borderRadius: '7px',
              bgcolor: noneSelected ? 'transparent' : '#16a34a',
              color: noneSelected ? '#94a3b8' : 'white',
              border: '1.5px solid',
              borderColor: noneSelected ? '#e2e8f0' : '#15803d',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#15803d', borderColor: '#166534', color: 'white', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: 'transparent', color: '#94a3b8', borderColor: '#e2e8f0' },
            }}
          >
            Approve
          </Button>
        </span>
      </Tooltip>

      {/* Reject */}
      <Tooltip title={noneSelected ? 'Select articles first' : ''}>
        <span>
          <Button
            size='small'
            startIcon={<Cancel sx={{ fontSize: 15 }} />}
            onClick={onBulkReject}
            disabled={noneSelected}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 1.5,
              py: 0.6,
              borderRadius: '7px',
              color: noneSelected ? '#94a3b8' : '#b91c1c',
              bgcolor: 'transparent',
              border: '1.5px solid',
              borderColor: noneSelected ? '#e2e8f0' : '#fca5a5',
              '&:hover': { bgcolor: '#fff5f5', borderColor: '#f87171' },
              '&.Mui-disabled': { color: '#94a3b8', borderColor: '#e2e8f0' },
            }}
          >
            Reject
          </Button>
        </span>
      </Tooltip>

      {/* Assign Country */}
      <Tooltip title={noneSelected ? 'Select articles first' : ''}>
        <span>
          <Button
            size='small'
            startIcon={<Public sx={{ fontSize: 15 }} />}
            onClick={onBulkAssignCountry}
            disabled={noneSelected}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 1.5,
              py: 0.6,
              borderRadius: '7px',
              color: noneSelected ? '#94a3b8' : themeColors.primary,
              bgcolor: 'transparent',
              border: '1.5px solid',
              borderColor: noneSelected ? '#e2e8f0' : '#cbd5e1',
              '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
              '&.Mui-disabled': { color: '#94a3b8', borderColor: '#e2e8f0' },
            }}
          >
            Assign Country
          </Button>


        </span>
      </Tooltip>

          <Divider orientation='vertical' flexItem sx={{ borderColor: '#e2e8f0', mx: 0.25 }} />

          {/* Select-all / Deselect-all button */}
          <Button
            size='small'
            startIcon={
              allSelected
                ? <DeselectIcon sx={{ fontSize: 16 }} />
                : <SelectAllIcon sx={{ fontSize: 16 }} />
            }
            onClick={allSelected ? onClearSelection : onSelectAll}
            variant='outlined'
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 1.4,
              py: 0.5,
              borderRadius: '7px',
              borderColor: themeColors.primary,
              color: themeColors.primary,
              '&:hover': { bgcolor: '#f1f5f9', borderColor: themeColors.primary },
            }}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </Button>

      <Box sx={{ flex: 1 }} />

      {!noneSelected && (
        <Typography
          variant='caption'
          sx={{ color: '#475569', fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
        >
          {selectedCount} of {totalCount} selected
        </Typography>
      )}
    </Box>
  )
}

// Main Component
const NewsManagementTab = ({ showNotification, fixedStatus }) => {
  const initialFilters = fixedStatus ? { status: fixedStatus } : undefined

  // NewsManagementTab grid: xs=1col, sm=2col, lg=3col
  const { pageSize: responsivePageSize, options: pageSizeOptions } = useResponsivePageSize(
    { xs: 1, sm: 2, md: 2, lg: 3 },
  )

  const {
    articles,
    loading,
    currentPage,
    totalPages,
    totalItems,
    filters,
    selectedIds,
    pageSize,
    loadArticles,
    approveArticle,
    rejectArticle,
    undoAction,
    updateArticleCountries,
    bulkApprove,
    bulkReject,
    bulkAssignCountry,
    toggleSelection,
    selectAll,
    clearSelection,
    handleFilterChange,
    handleFiltersChange,
    handleSearch,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
  } = useNewsManagement(initialFilters, responsivePageSize)

  // Sync page size when screen resizes
  useEffect(() => {
    handlePageSizeChange(responsivePageSize)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsivePageSize])

  const activeStatuses = useMemo(
    () => [...new Set(articles.map((a) => a.reviewStatus).filter(Boolean))],
    [articles],
  )
  const isAutoRejectedView = activeStatuses.length > 0
    ? activeStatuses.every((s) => s === NEWS_REVIEW_STATUS.AUTO_REJECTED)
    : fixedStatus === NEWS_REVIEW_STATUS.AUTO_REJECTED

  // Bulk assign country dialog
  const [assignCountryDialog, setAssignCountryDialog] = useState({
    open: false,
    selectedCountries: [],
  })

  // Per-card action loading state
  const [actionLoading, setActionLoading] = useState(new Set())

  // Notification helper
  const notify = (message, severity = 'success') => {
    showNotification?.(message, severity)
  }

  useEffect(() => {
    loadArticles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Per-card actions

  const withLoading = async (id, fn, successMsg) => {
    setActionLoading((prev) => new Set([...prev, id]))
    try {
      await fn()
      notify(successMsg)
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Action failed'
      notify(msg, 'error')
    } finally {
      setActionLoading((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const handleApprove = (id) =>
    withLoading(id, () => approveArticle(id), 'Article approved - it will appear on the platform.')

  const handleReject = (id) =>
    withLoading(id, () => rejectArticle(id), 'Article rejected and removed from review queue.')

  const handleUndo = (id) =>
    withLoading(id, () => undoAction(id), 'Action undone - article returned to Pending Review.')

  const handleUpdateCountries = async (id, countries) => {
    try {
      await updateArticleCountries(id, countries)
      notify('Countries updated.')
    } catch {
      notify('Failed to update countries.', 'error')
    }
  }

  const handleBulkApprove = async () => {
    if (!selectedIds.size) return
    try {
      await bulkApprove(selectedIds)
      notify(`${selectedIds.size} article${selectedIds.size !== 1 ? 's' : ''} approved.`)
    } catch {
      notify('Bulk approve failed.', 'error')
    }
  }

  const handleBulkReject = async () => {
    if (!selectedIds.size) return
    try {
      await bulkReject(selectedIds)
      notify(`${selectedIds.size} article${selectedIds.size !== 1 ? 's' : ''} rejected.`)
    } catch {
      notify('Bulk reject failed.', 'error')
    }
  }

  const openAssignCountryDialog = () => {
    setAssignCountryDialog({ open: true, selectedCountries: [] })
  }

  const closeAssignCountryDialog = () => {
    setAssignCountryDialog({ open: false, selectedCountries: [] })
  }

  const confirmBulkAssignCountry = async () => {
    const { selectedCountries } = assignCountryDialog
    if (!selectedCountries.length) return
    closeAssignCountryDialog()
    try {
      await bulkAssignCountry(selectedIds, selectedCountries)
      const label = selectedCountries.join(', ')
      notify(`${label} added to ${selectedIds.size} article${selectedIds.size !== 1 ? 's' : ''}.`)
    } catch {
      notify('Bulk country assignment failed.', 'error')
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, color: themeColors.primary }}>
            News Management
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
            {isAutoRejectedView
              ? 'Auto-rejected articles. Promote to Pending Review or Approve directly.'
              : 'Review low-confidence articles before they appear on the platform.'}
            {!loading && totalItems > 0 && (
              <Box component='span' sx={{ fontWeight: 600, color: themeColors.primary, ml: 0.75 }}>
                {totalItems} article{totalItems !== 1 ? 's' : ''} found.
              </Box>
            )}
          </Typography>
        </Box>

      </Box>

      {/* Filters */}
      <NewsFiltersBar
        filters={filters}
        articles={articles}
        onFilterChange={handleFilterChange}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onSort={handleSortChange}
        onReset={resetFilters}
        hideStatusFilter={!!fixedStatus}
      />

      {/* Persistent selection + bulk action bar */}
      {!loading && articles.length > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          totalCount={articles.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onBulkApprove={handleBulkApprove}
          onBulkReject={handleBulkReject}
          onBulkAssignCountry={openAssignCountryDialog}
        />
      )}

      {/* Content: Loading / Empty / Grid */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : articles.length === 0 ? (
        <EmptyState filters={filters} />
      ) : (
        <Grid container spacing={2}>
          {articles.map((article) => (
            <Grid item xs={12} sm={6} lg={4} key={article.id} sx={{ display: 'flex' }}>
              <NewsArticleCard
                article={article}
                selected={selectedIds.has(article.id)}
                onToggleSelect={toggleSelection}
                onApprove={handleApprove}
                onReject={handleReject}
                onUndo={handleUndo}
                onUpdateCountries={handleUpdateCountries}
                disabled={actionLoading.has(article.id)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination + Items per page */}
      {totalItems > 0 && (
        <Box sx={{ mt: 2 }}>
          {totalPages > 1 && (
            <Pagination
              itemsPerPage={pageSize}
              totalItems={totalItems}
              currentPage={currentPage}
              paginate={handlePageChange}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, px: 0.5 }}>
            <ItemsPerPageSelector
              itemsPerPage={pageSize}
              setItemsPerPage={handlePageSizeChange}
              loading={loading}
              options={pageSizeOptions}
            />
          </Box>
        </Box>
      )}

      {/* Bulk Assign Country Dialog */}
      <Dialog
        open={assignCountryDialog.open}
        onClose={closeAssignCountryDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: themeColors.primary }}>
          Assign Countries to {selectedIds.size} Article{selectedIds.size !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Select one or more countries to <strong>add</strong> to all selected articles. Existing
            countries will be preserved.
          </Typography>
          <FormControl fullWidth size='small'>
            <InputLabel id='bulk-country-select-label'>Countries</InputLabel>
            <Select
              labelId='bulk-country-select-label'
              multiple
              value={assignCountryDialog.selectedCountries}
              label='Countries'
              onChange={(e) =>
                setAssignCountryDialog((prev) => ({
                  ...prev,
                  selectedCountries: e.target.value,
                }))
              }
              renderValue={(selected) => selected.join(', ')}
              MenuProps={{ disableScrollLock: true }}
            >
              {GCC_COUNTRIES.map((c) => (
                <MenuItem key={c} value={c} sx={{ py: 0.5, display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={assignCountryDialog.selectedCountries.includes(c)}
                    size='small'
                    sx={{ p: 0.5, '&.Mui-checked': { color: themeColors.primary } }}
                  />
                  {GCC_FLAG_CODES[c] && (
                    <img
                      loading='lazy'
                      width='20'
                      src={`https://flagcdn.com/w20/${GCC_FLAG_CODES[c]}.png`}
                      srcSet={`https://flagcdn.com/w40/${GCC_FLAG_CODES[c]}.png 2x`}
                      alt=''
                      style={{ flexShrink: 0, marginRight: 6 }}
                    />
                  )}
                  <ListItemText primary={c} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={closeAssignCountryDialog}
            sx={{ textTransform: 'none', color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmBulkAssignCountry}
            variant='contained'
            disabled={!assignCountryDialog.selectedCountries.length}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: themeColors.primary,
              '&:hover': { bgcolor: themeColors.hover },
              '&.Mui-disabled': { bgcolor: '#e2e8f0' },
            }}
          >
            Add Country
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default NewsManagementTab
